import { useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";
import clsx from "clsx";
import type { PlanRecalculateProps } from "./types";

export const PlanRecalculate = ({
  isStale,
  isRunning,
  summary,
  onRecalculate,
  changedCount,
  onRecalculateChanged,
  onDismissSummary,
  formatMetres,
}: PlanRecalculateProps) => {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);

  if (summary) {
    const moved = summary.filter(
      (change) => Math.abs(change.afterM - change.beforeM) >= 0.05,
    );
    const delta = summary.reduce(
      (sum, change) => sum + (change.afterM - change.beforeM),
      0,
    );
    return (
      <div className="mb-2 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[13px] text-slate-800">
        <div className="flex items-start justify-between gap-2">
          {/* Its own block line, never a bare string in a flex row: an
              interpolated string there becomes an anonymous flex item and wraps
              word by word into ragged columns. */}
          <div className="min-w-0">
            <div className="truncate whitespace-nowrap font-semibold">
              {t("smartSurveyor.cablesBeta.planRecalcDone", {
                count: moved.length,
              })}
            </div>
            <div className="truncate whitespace-nowrap text-slate-600">
              {t("smartSurveyor.cablesBeta.planRecalcDelta", {
                delta: `${delta >= 0 ? "+" : "−"}${formatMetres(Math.abs(delta))}`,
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={onDismissSummary}
            title={t("smartSurveyor.cablesBeta.planRecalcDismiss")}
            className="flex-none rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            <X size={15} />
          </button>
        </div>
        {/* The runs that moved MOST, not all of them: eighty-three identical
            rows is the wrong shape for a summary, and the ones worth his eye
            are the ones that moved. */}
        {moved.length > 0 && (
          <ul className="mt-1.5 space-y-0.5 text-[12px] text-slate-600">
            {[...moved]
              .sort(
                (a, b) =>
                  Math.abs(b.afterM - b.beforeM) - Math.abs(a.afterM - a.beforeM),
              )
              .slice(0, 5)
              .map((change) => (
                <li
                  key={change.circuitId}
                  className="truncate whitespace-nowrap"
                >
                  {change.circuitId} · {formatMetres(change.beforeM)} →{" "}
                  {formatMetres(change.afterM)}
                </li>
              ))}
          </ul>
        )}
      </div>
    );
  }

  if (isConfirming) {
    return (
      <div className="mb-2 rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
        <div className="truncate whitespace-nowrap font-semibold">
          {t("smartSurveyor.cablesBeta.planRecalcConfirmTitle")}
        </div>
        <div className="mt-0.5 text-[12px] text-amber-800">
          {t("smartSurveyor.cablesBeta.planRecalcConfirmBody")}
        </div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => {
              setIsConfirming(false);
              onRecalculate();
            }}
            className="flex min-h-[34px] flex-1 items-center justify-center rounded-md bg-amber-600 px-3 text-[13px] font-semibold text-white hover:bg-amber-700"
          >
            {t("smartSurveyor.cablesBeta.planRecalcConfirmGo")}
          </button>
          <button
            type="button"
            onClick={() => setIsConfirming(false)}
            className="flex min-h-[34px] items-center justify-center rounded-md border border-amber-300 bg-white px-3 text-[13px] font-semibold text-amber-900 hover:bg-amber-100"
          >
            {t("smartSurveyor.cablesBeta.planRecalcConfirmCancel")}
          </button>
        </div>
      </div>
    );
  }

  // Edited runs are re-asked at once; the whole plan moves every run he signs, so it confirms first.
  const isChangesOnly = !isStale && changedCount > 0;
  const needsAttention = isStale || isChangesOnly;

  return (
    <button
      type="button"
      disabled={isRunning}
      data-stale={needsAttention || undefined}
      onClick={() => (isChangesOnly ? onRecalculateChanged() : setIsConfirming(true))}
      className={clsx(
        "mb-2 flex min-h-[36px] w-full items-center justify-center gap-2 rounded-lg border px-3 py-1.5 text-[13px] font-semibold disabled:border-slate-200 disabled:text-slate-400",
        needsAttention && !isRunning
          ? "border-amber-400 bg-amber-50 text-amber-900 hover:border-amber-500 hover:bg-amber-100"
          : "border-slate-300 bg-white text-slate-800 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700",
      )}
    >
      <RefreshCw
        size={15}
        className={clsx("flex-none", isRunning && "animate-spin")}
      />
      <span className="truncate whitespace-nowrap">
        {t(
          isRunning
            ? "smartSurveyor.cablesBeta.planRecalcRunning"
            : isStale
              ? "smartSurveyor.cablesBeta.planRecalcStale"
              : isChangesOnly
                ? "smartSurveyor.cablesBeta.planRecalcChanged"
                : "smartSurveyor.cablesBeta.planRecalc",
          isChangesOnly ? { count: changedCount } : undefined,
        )}
      </span>
    </button>
  );
};
